import { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteConfig } from '@/mocks/homeData';
import { aboutData } from '@/mocks/aboutData';

export default function AboutPage() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [activeSection, setActiveSection] = useState<string>('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快与您联系。');
      setEmail('');
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    const data = new URLSearchParams();
    data.append('email', email);
    data.append('type', 'cooperation');

    try {
      const response = await fetch('http://shangremen.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！我们会尽快与您联系。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        setActiveSection(hash);
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const schema = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: `关于我们 - ${siteConfig.name}`,
      description: aboutData.brandIntro.paragraphs[0],
      url: `${siteConfig.url}/about`,
      isPartOf: {
        '@type': 'WebSite',
        name: siteConfig.name,
        url: siteConfig.url,
      },
      mainEntity: {
        '@type': 'Organization',
        name: siteConfig.name,
        description: aboutData.brandIntro.paragraphs[0],
        url: siteConfig.url,
        email: aboutData.contact.email,
        address: {
          '@type': 'PostalAddress',
          streetAddress: aboutData.contact.address,
          addressCountry: 'CN',
        },
      },
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = schema;
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24">
        <section className="py-12 md:py-20 px-4 md:px-6">
          <div className="max-w-screen-xl mx-auto">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-foreground-800 transition-colors duration-200">
                    首页
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li className="text-foreground-800 font-medium">关于我们</li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-label mb-4">
                  品牌介绍
                </span>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground-950 leading-tight mb-6">
                  {aboutData.brandIntro.title}
                </h1>
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-4">
                  {aboutData.brandIntro.paragraphs[0]}
                </p>
                {aboutData.brandIntro.paragraphs.slice(1).map((p, idx) => (
                  <p key={idx} className="text-sm md:text-base text-foreground-500 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
              </div>

              <div className="rounded-2xl bg-secondary-900 p-6 md:p-8 text-background-50">
                <span className="text-xs text-secondary-400 uppercase tracking-wider">平台数据</span>
                <h3 className="font-heading text-xl font-bold mt-2 mb-6">{aboutData.reputation.title}</h3>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {aboutData.reputation.stats.map((stat) => (
                    <div key={stat.label} className="rounded-lg bg-secondary-800/50 p-4">
                      <div className="text-2xl md:text-3xl font-heading font-black text-accent-400">
                        {stat.value}
                      </div>
                      <div className="text-xs text-secondary-300 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {aboutData.reputation.highlights.map((item) => (
                    <div key={item} className="flex items-start gap-2">
                      <i className="ri-check-line text-accent-400 mt-0.5"></i>
                      <span className="text-sm text-secondary-200">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className={`py-12 md:py-16 px-4 md:px-6 bg-background-100/50 transition-all duration-300 ${activeSection === 'contact' ? 'ring-2 ring-accent-300/50 rounded-2xl' : ''}`}>
          <div className="max-w-screen-xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100/80 text-accent-800 text-xs font-label mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 leading-tight mb-4">
                  {aboutData.contact.title}
                </h2>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-8">
                  {aboutData.contact.description}
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-mail-line text-secondary-600"></i>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400 block">邮箱</span>
                      <span className="text-sm text-foreground-800">{aboutData.contact.email}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-wechat-line text-secondary-600"></i>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400 block">微信公众号</span>
                      <span className="text-sm text-foreground-800">{aboutData.contact.wechat}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400 block">地址</span>
                      <span className="text-sm text-foreground-800">{aboutData.contact.address}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {aboutData.contact.cooperation.map((item) => (
                    <span key={item} className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl border border-foreground-200/40 p-6 md:p-8">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  商务合作意向
                </h3>
                <p className="text-sm text-foreground-500 mb-6">
                  留下您的邮箱，我们的商务团队会在24小时内与您联系。
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs text-foreground-500 block mb-1.5">邮箱地址</label>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="hello@shangremen.cn"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-foreground-200/60 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors duration-200"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full px-6 py-3 rounded-full bg-primary-900 text-background-50 text-sm font-label font-medium hover:bg-primary-800 disabled:opacity-60 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '提交中...' : '提交合作意向'}
                    <i className="ri-arrow-right-line ml-1"></i>
                  </button>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-alt-field"
                  />

                  {formStatus === 'success' && (
                    <p className="text-sm text-secondary-600">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-accent-600">{formMessage}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section id="disclaimer" className={`py-12 md:py-16 px-4 md:px-6 ${activeSection === 'disclaimer' ? 'ring-2 ring-accent-300/50 rounded-2xl' : ''}`}>
          <div className="max-w-screen-xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">
              {aboutData.disclaimer.title}
            </h2>
            <div className="space-y-4">
              {aboutData.disclaimer.content.map((p, idx) => (
                <p key={idx} className="text-sm text-foreground-500 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>

        <section id="privacy" className={`py-12 md:py-16 px-4 md:px-6 bg-background-100/50 ${activeSection === 'privacy' ? 'ring-2 ring-accent-300/50 rounded-2xl' : ''}`}>
          <div className="max-w-screen-xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">
              {aboutData.privacy.title}
            </h2>
            <div className="space-y-4">
              {aboutData.privacy.content.map((p, idx) => (
                <p key={idx} className="text-sm text-foreground-500 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>

        <section id="terms" className={`py-12 md:py-16 px-4 md:px-6 ${activeSection === 'terms' ? 'ring-2 ring-accent-300/50 rounded-2xl' : ''}`}>
          <div className="max-w-screen-xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">
              用户协议
            </h2>
            <div className="space-y-4 text-sm text-foreground-500 leading-relaxed">
              <p>
                欢迎访问吃瓜网在线！在使用本平台服务前，请仔细阅读以下用户协议。您一旦使用本平台服务，即视为同意本协议所有条款。
              </p>
              <p>
                <strong className="text-foreground-700">第一条 服务内容</strong><br />
                本平台提供娱乐八卦资讯聚合服务，包括但不限于文章阅读、视频观看、爆料追踪等内容。我们保留随时修改或中断服务的权利。
              </p>
              <p>
                <strong className="text-foreground-700">第二条 用户行为规范</strong><br />
                用户在使用本平台服务时，应遵守国家法律法规，不得发布违法、违规或侵犯他人合法权益的内容。禁止利用本平台从事任何非法活动。
              </p>
              <p>
                <strong className="text-foreground-700">第三条 知识产权</strong><br />
                本平台上的所有内容（包括但不限于文字、图片、视频等）的知识产权归本平台或原作者所有。未经授权，任何单位或个人不得擅自转载、复制或用于商业用途。
              </p>
              <p>
                <strong className="text-foreground-700">第四条 免责声明</strong><br />
                对于因不可抗力或本平台无法控制的原因造成的服务中断或其他缺陷，本平台不承担任何责任。
              </p>
              <p>
                <strong className="text-foreground-700">第五条 协议修改</strong><br />
                本平台有权在必要时修改本协议条款。修改后的协议一旦在本平台公布即有效。用户继续使用本平台服务，视为接受修改后的协议。
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}